import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useLocations } from '../hooks/useLocations';
import { useBoxSettings } from '../hooks/useBoxSettings';
import type { Location } from '@deposits/shared';

const SELECTED_LOCATION_KEY = 'deposits.selected.location_id';

interface LocationContextValue {
  locations: Location[];
  isLoading: boolean;
  selectedLocationId: number | null;
  setSelectedLocationId: (id: number) => void;
  selectedLocation: Location | null;
  /** Whether the box separates data per location. When false the picker is
   *  hidden and selectedLocationId is forced to a single canonical location. */
  manageLocations: boolean;
}

const LocationContext = createContext<LocationContextValue | null>(null);

export function LocationProvider({ children }: { children: ReactNode }) {
  const { data: locations = [], isLoading } = useLocations();
  const { data: settings } = useBoxSettings();
  const manageLocations = settings?.manage_locations ?? true;
  const [selectedLocationId, setSelectedLocationIdState] = useState<number | null>(() => {
    const cached = localStorage.getItem(SELECTED_LOCATION_KEY);
    return cached ? Number(cached) : null;
  });

  // Validate stored id on each locations fetch. If the cached id no longer
  // exists (location was removed), fall back to locations[0].id. Always wrap
  // both sides in String(...) — pg returns IDs that can drift between number
  // and string across URL/localStorage roundtrips.
  // See docs/platform/lockers/locations_addition_instructions.md §10.
  useEffect(() => {
    if (locations.length === 0) return;
    const stillValid =
      selectedLocationId != null &&
      locations.some((l) => String(l.id) === String(selectedLocationId));
    if (!stillValid) {
      const first = locations[0]!.id;
      setSelectedLocationIdState(first);
      localStorage.setItem(SELECTED_LOCATION_KEY, String(first));
    }
  }, [locations, selectedLocationId]);

  const setSelectedLocationId = (id: number) => {
    setSelectedLocationIdState(id);
    localStorage.setItem(SELECTED_LOCATION_KEY, String(id));
  };

  // When locations aren't managed, force the canonical (first) location so the
  // whole app buckets under one location regardless of the stored selection.
  const effectiveSelectedLocationId = manageLocations
    ? selectedLocationId
    : locations[0]?.id ?? null;

  const selectedLocation = useMemo(
    () =>
      locations.find((l) => String(l.id) === String(effectiveSelectedLocationId)) ?? null,
    [locations, effectiveSelectedLocationId],
  );

  const value: LocationContextValue = {
    locations,
    isLoading,
    selectedLocationId: effectiveSelectedLocationId,
    setSelectedLocationId,
    selectedLocation,
    manageLocations,
  };
  return (
    <LocationContext.Provider value={value}>{children}</LocationContext.Provider>
  );
}

export function useLocationContext(): LocationContextValue {
  const ctx = useContext(LocationContext);
  if (!ctx) throw new Error('useLocationContext must be used inside LocationProvider');
  return ctx;
}
